import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDrawing, SerializedDrawing, FIBONACCI_LEVELS } from 'lightweight-charts-drawing';
import { ChartDrawingController } from '../chart-drawing-controller';
import { getToolLabel, getToolMeta, SettingsFieldDef, ToolUiMeta, TV_PALETTE } from '../drawing-tools.catalog';

export interface DrawingSettingsDialogData {
  controller: ChartDrawingController;
  drawingId: string;
}

interface AnchorRow {
  price: number;
  /** valor para <input type="datetime-local"> */
  datetime: string;
}

@Component({
  selector: 'app-drawing-settings-dialog',
  templateUrl: './drawing-settings-dialog.component.html',
  styleUrls: ['./drawing-settings-dialog.component.scss'],
})
export class DrawingSettingsDialogComponent implements OnInit {
  public drawing: IDrawing | null = null;
  public meta: ToolUiMeta | undefined;
  public title = '';
  public tab: 'style' | 'coords' | 'visibility' = 'style';
  public palette = TV_PALETTE;

  // modelo editable
  public lineColor = '#2962FF';
  public lineWidth = 2;
  public lineDashKey: 'solid' | 'dashed' | 'dotted' = 'solid';
  public fillColor = '';
  public fillOpacity = 0.2;
  public showLabels = true;
  public extraValues: Record<string, any> = {};
  public levels: number[] = [];
  public anchors: AnchorRow[] = [];
  public visible = true;
  public locked = false;
  public zIndex = 0;

  private snapshot: SerializedDrawing | null = null;
  private controller!: ChartDrawingController;

  constructor(
    public dialogRef: MatDialogRef<DrawingSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DrawingSettingsDialogData
  ) {}

  ngOnInit(): void {
    this.controller = this.data.controller;
    this.drawing = this.controller.getById(this.data.drawingId) ?? null;
    if (!this.drawing) { this.dialogRef.close(); return; }

    this.meta = getToolMeta(this.drawing.type);
    this.title = getToolLabel(this.drawing.type);
    this.snapshot = this.drawing.toJSON();

    const style = this.drawing.style;
    this.lineColor = style.lineColor ?? '#2962FF';
    this.lineWidth = style.lineWidth ?? 2;
    this.lineDashKey = this.dashToKey(style.lineDash);
    this.fillColor = style.fillColor ?? '';
    this.fillOpacity = style.fillOpacity ?? 0.2;
    this.showLabels = style.showLabels !== false;

    const options = this.drawing.options as Record<string, any>;
    this.visible = options['visible'] !== false;
    this.locked = options['locked'] === true;
    this.zIndex = options['zIndex'] ?? 0;
    this.levels = Array.isArray(options['levels']) ? [...options['levels']] : [];

    for (const field of this.extraFields) {
      if (field.kind === 'levels') continue;
      this.extraValues[field.key] = field.target === 'style'
        ? (style as any)[field.key]
        : options[field.key];
    }

    this.anchors = this.drawing.anchors.map(a => ({
      price: a.price,
      datetime: this.msToLocalInput(this.controller.anchorTimeToMs(a.time)),
    }));
  }

  public get extraFields(): SettingsFieldDef[] {
    return this.meta?.settingsFields ?? [];
  }

  public get hasLevels(): boolean {
    return this.extraFields.some(f => f.kind === 'levels');
  }

  // ── aplicación en vivo ───────────────────────────────────────────────

  public applyStyle(): void {
    if (!this.drawing) return;
    this.controller.updateStyle(this.drawing.id, {
      lineColor: this.lineColor,
      lineWidth: Number(this.lineWidth) || 1,
      lineDash: this.keyToDash(this.lineDashKey),
      fillColor: this.fillColor || undefined,
      fillOpacity: Number(this.fillOpacity),
      showLabels: this.showLabels,
    }, false);
  }

  public applyField(field: SettingsFieldDef): void {
    if (!this.drawing) return;
    let value = this.extraValues[field.key];
    if (field.kind === 'number') value = Number(value);
    if (field.target === 'style') {
      this.controller.updateStyle(this.drawing.id, { [field.key]: value } as any, false);
    } else {
      this.controller.updateOptions(this.drawing.id, { [field.key]: value } as any, false);
    }
  }

  public applyLevels(): void {
    if (!this.drawing) return;
    const clean = this.levels.map(Number).filter(v => Number.isFinite(v));
    this.controller.updateOptions(this.drawing.id, { levels: clean } as any, false);
  }

  public addLevel(): void {
    this.levels.push(0);
  }

  public removeLevel(index: number): void {
    this.levels.splice(index, 1);
    this.applyLevels();
  }

  public resetLevels(): void {
    const original = (this.snapshot?.options as any)?.levels;
    this.levels = Array.isArray(original) && original.length ? [...original] : [...FIBONACCI_LEVELS];
    this.applyLevels();
  }

  public applyAnchors(): void {
    if (!this.drawing) return;
    const anchors = this.anchors.map((row, i) => {
      const ms = Date.parse(row.datetime);
      return {
        time: Number.isFinite(ms) ? this.controller.msToAnchorTime(ms) : this.drawing!.anchors[i].time,
        price: Number(row.price),
      };
    });
    try {
      this.drawing.setAnchors(anchors as any);
      this.drawing.requestUpdate();
    } catch {}
  }

  public applyVisibility(): void {
    if (!this.drawing) return;
    this.controller.updateOptions(this.drawing.id, {
      visible: this.visible,
      locked: this.locked,
      zIndex: Number(this.zIndex) || 0,
    }, false);
  }

  public trackByIndex(index: number): number {
    return index;
  }

  // ── cierre ───────────────────────────────────────────────────────────

  public accept(): void {
    if (this.drawing && this.snapshot) {
      this.controller.commitEdit(this.drawing.id, this.snapshot);
    }
    this.dialogRef.close(true);
  }

  public cancel(): void {
    if (this.drawing && this.snapshot) {
      this.controller.applySnapshot(this.drawing.id, this.snapshot);
    }
    this.dialogRef.close(false);
  }

  // ── helpers ──────────────────────────────────────────────────────────

  private dashToKey(dash?: number[]): 'solid' | 'dashed' | 'dotted' {
    if (!dash || dash.length === 0) return 'solid';
    return dash[0] <= 3 ? 'dotted' : 'dashed';
  }

  private keyToDash(key: 'solid' | 'dashed' | 'dotted'): number[] {
    switch (key) {
      case 'dashed': return [6, 4];
      case 'dotted': return [2, 2];
      default: return [];
    }
  }

  private msToLocalInput(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
